import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from '@wwe-portal/web/libs/bridge/local-storage';
import { useCallback, useState } from 'react';

const tryGetPersistData = <T>(
  key: string,
  typeGuard: (a: unknown) => boolean
) => {
  try {
    const value = getStorageItem(key);
    if (!value) {
      return null;
    }
    const data = JSON.parse(value);
    if (typeGuard(data)) {
      return data as T;
    }
    return null;
  } catch {
    return null;
  }
};

const persist = <T>(key: string, data: T) => {
  setStorageItem(key, JSON.stringify(data));
};

const clearPersist = (key: string) => {
  removeStorageItem(key);
};

export const usePersistentData = <T>(
  key: string,
  typeGuard: (value: unknown) => boolean
) => {
  const initialData = tryGetPersistData<T>(key, typeGuard);
  const [data, setState] = useState(initialData);
  const persistData = useCallback(
    (data: T | null) => {
      persist(key, data);
      setState(data);
    },
    [key]
  );

  const clearData = useCallback(() => {
    clearPersist(key);
    setState(null);
  }, [key]);

  const getData = useCallback(
    () => tryGetPersistData<T>(key, typeGuard),
    [key]
  );

  return {
    data,
    getData,
    clearData,
    persistData,
  };
};
